import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ShieldLogo } from "../components/landing/Logo";
import { 
  Mic, Square, Upload, Loader2, ChefHat, Clock, DollarSign, 
  Save, FileText, Trash2, Edit3, X, Download, 
  UtensilsCrossed, Wine, Sparkles, AlertTriangle, Home, Plus,
  Shield, AlertCircle, Info, CheckCircle2, XCircle, Crown, Eye, Volume2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Department icons and colors
const DEPARTMENTS = {
  boh: { label: "Back of House", icon: ChefHat, color: "text-orange-400", bg: "bg-orange-400/10" },
  foh: { label: "Front of House", icon: UtensilsCrossed, color: "text-blue-400", bg: "bg-blue-400/10" },
  bar: { label: "Bar", icon: Wine, color: "text-purple-400", bg: "bg-purple-400/10" },
  dish: { label: "Dish Pit", icon: Sparkles, color: "text-cyan-400", bg: "bg-cyan-400/10" },
};

// Audio Recorder Component
const AudioRecorder = ({ onRecordingComplete, isProcessing }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        onRecordingComplete(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch (err) {
      toast.error("Microphone access denied. Please allow microphone permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <motion.button
        data-testid="record-btn"
        onClick={isRecording ? stopRecording : startRecording}
        disabled={isProcessing}
        className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
          isRecording 
            ? "bg-red-500 animate-pulse" 
            : "bg-gold hover:bg-gold/80"
        } ${isProcessing ? "opacity-50 cursor-not-allowed" : ""}`}
        whileHover={{ scale: isProcessing ? 1 : 1.05 }}
        whileTap={{ scale: isProcessing ? 1 : 0.95 }}
      >
        {isProcessing ? (
          <Loader2 className="w-10 h-10 text-indigo-700 animate-spin" />
        ) : isRecording ? (
          <Square className="w-10 h-10 text-white" />
        ) : (
          <Mic className="w-10 h-10 text-indigo-700" />
        )}
      </motion.button>
      
      {isRecording && (
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <p className="text-red-400 font-mono text-xl">{formatTime(recordingTime)}</p>
          <p className="text-xs text-slate-400 mt-1">Recording... Click to stop</p>
        </motion.div>
      )}
      
      {!isRecording && !isProcessing && (
        <p className="text-xs text-slate-400">Click to start recording your SOP</p>
      )}
      
      {isProcessing && (
        <p className="text-xs text-gold animate-pulse">Processing audio...</p>
      )}
    </div>
  );
};

// File Upload Component
const FileUploader = ({ onFileSelect, isProcessing }) => {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 25 * 1024 * 1024) {
        toast.error("File too large. Maximum size is 25MB.");
        return;
      }
      onFileSelect(file);
    }
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        onChange={handleFileChange}
        className="hidden"
        data-testid="file-input"
      />
      <Button
        data-testid="upload-btn"
        onClick={() => fileInputRef.current?.click()}
        disabled={isProcessing}
        variant="outline"
        className="border-gold/30 text-gold hover:bg-gold/10"
      >
        <Upload className="w-4 h-4 mr-2" />
        Upload Voice Memo
      </Button>
      <p className="text-[10px] text-slate-500">MP3, WAV, M4A up to 25MB</p>
    </div>
  );
};

// SOP Preview Card Component
const SOPPreviewCard = ({ sop, onSave, onEdit, isEditing, editedSop, setEditedSop, onCancel, isSaving, isAuditing }) => {
  const dept = DEPARTMENTS[sop.department] || DEPARTMENTS.boh;
  const DeptIcon = dept.icon;

  if (isEditing) {
    return (
      <motion.div 
        className="glass-card rounded-xl p-6 border border-gold/20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300 text-xs">SOP Title</Label>
              <Input
                value={editedSop.title}
                onChange={(e) => setEditedSop({ ...editedSop, title: e.target.value })}
                className="mt-1 bg-indigo-700/50 border-gold/20 text-white"
                data-testid="edit-title"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Department</Label>
              <Select value={editedSop.department} onValueChange={(v) => setEditedSop({ ...editedSop, department: v })}>
                <SelectTrigger className="mt-1 bg-indigo-700/50 border-gold/20 text-white" data-testid="edit-department">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-indigo-700 border-gold/20">
                  {Object.entries(DEPARTMENTS).map(([key, { label }]) => (
                    <SelectItem key={key} value={key} className="text-white">{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300 text-xs">Prep Time</Label>
              <Input
                value={editedSop.prep_time}
                onChange={(e) => setEditedSop({ ...editedSop, prep_time: e.target.value })}
                className="mt-1 bg-indigo-700/50 border-gold/20 text-white"
                placeholder="e.g., 15 minutes"
                data-testid="edit-prep-time"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Yield/Servings</Label>
              <Input
                value={editedSop.yield}
                onChange={(e) => setEditedSop({ ...editedSop, yield: e.target.value })}
                className="mt-1 bg-indigo-700/50 border-gold/20 text-white"
                placeholder="e.g., 10 servings"
                data-testid="edit-yield"
              />
            </div>
          </div>

          <div>
            <Label className="text-slate-300 text-xs">Ingredients (one per line: qty unit item)</Label>
            <Textarea
              value={editedSop.ingredients?.map(i => `${i.qty} ${i.unit} ${i.item}`).join('\n') || ''}
              onChange={(e) => {
                const lines = e.target.value.split('\n');
                const ingredients = lines.map(line => {
                  const parts = line.trim().split(/\s+/);
                  if (parts.length >= 3) {
                    return { qty: parts[0], unit: parts[1], item: parts.slice(2).join(' ') };
                  } else if (parts.length === 2) {
                    return { qty: parts[0], unit: '', item: parts[1] };
                  }
                  return { qty: '', unit: '', item: line };
                }).filter(i => i.item);
                setEditedSop({ ...editedSop, ingredients });
              }}
              className="mt-1 bg-indigo-700/50 border-gold/20 text-white h-32"
              placeholder="2 cups flour&#10;1 tbsp salt&#10;500 g chicken"
              data-testid="edit-ingredients"
            />
          </div>

          <div>
            <Label className="text-slate-300 text-xs">Steps (one per line)</Label>
            <Textarea
              value={editedSop.steps?.join('\n') || ''}
              onChange={(e) => setEditedSop({ ...editedSop, steps: e.target.value.split('\n').filter(s => s.trim()) })}
              className="mt-1 bg-indigo-700/50 border-gold/20 text-white h-40"
              placeholder="Preheat oven to 350°F&#10;Mix dry ingredients&#10;Add wet ingredients..."
              data-testid="edit-steps"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={onSave} disabled={isSaving || isAuditing} className="bg-lime hover:bg-lime/80 text-indigo-700" data-testid="save-sop-btn">
              {(isSaving || isAuditing) ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
              {isAuditing ? "Auditing..." : "Review & Save"}
            </Button>
            <Button onClick={onCancel} variant="outline" className="border-slate-500 text-slate-300" data-testid="cancel-edit-btn">
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="glass-card rounded-xl overflow-hidden border border-gold/20"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="sop-preview-card"
    >
      {/* Header */}
      <div className={`p-4 ${dept.bg} border-b border-gold/10`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <DeptIcon className={`w-6 h-6 ${dept.color}`} />
            <div>
              <h3 className="font-inter font-bold text-white text-lg">{sop.title || "Untitled SOP"}</h3>
              <span className={`text-xs ${dept.color}`}>{dept.label}</span>
            </div>
          </div>
          <Button onClick={onEdit} variant="ghost" size="sm" className="text-slate-400 hover:text-white" data-testid="edit-btn">
            <Edit3 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Meta Info */}
      <div className="p-4 grid grid-cols-3 gap-4 border-b border-gold/10">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gold" />
          <div>
            <p className="text-[10px] text-slate-400">Prep Time</p>
            <p className="text-sm text-white font-medium">{sop.prep_time || "—"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-gold" />
          <div>
            <p className="text-[10px] text-slate-400">Yield</p>
            <p className="text-sm text-white font-medium">{sop.yield || "—"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-lime" />
          <div>
            <p className="text-[10px] text-slate-400">Batch Cost</p>
            <p className="text-sm text-lime font-bold">${sop.total_cost?.toFixed(2) || "0.00"}</p>
          </div>
        </div>
      </div>

      {/* Ingredients */}
      {sop.ingredients?.length > 0 && (
        <div className="p-4 border-b border-gold/10">
          <h4 className="text-xs font-bold text-gold uppercase tracking-wider mb-3">Ingredients</h4>
          <div className="grid grid-cols-2 gap-2">
            {sop.ingredients.map((ing, i) => (
              <div key={i} className="flex justify-between text-sm bg-indigo-700/20 rounded px-2 py-1">
                <span className="text-slate-300">{ing.item}</span>
                <span className="text-gold font-mono">{ing.qty} {ing.unit}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Steps */}
      {sop.steps?.length > 0 && (
        <div className="p-4">
          <h4 className="text-xs font-bold text-gold uppercase tracking-wider mb-3">Procedure</h4>
          <ol className="space-y-2">
            {sop.steps.map((step, i) => (
              <li key={i} className="flex gap-3 text-sm">
                <span className="w-6 h-6 rounded-full bg-gold/20 text-gold flex items-center justify-center shrink-0 text-xs font-bold">
                  {i + 1}
                </span>
                <span className="text-slate-300 leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Cost Breakdown + Target Menu Price */}
      {sop.cost_per_serving > 0 && (
        <div className="p-4 bg-lime/5 border-t border-lime/20">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Cost Per Serving</p>
              <p className="text-xl font-bold text-lime">${sop.cost_per_serving?.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Target Menu Price</p>
              <p className="text-xl font-bold text-gold">${sop.target_menu_price?.toFixed(2) || (sop.cost_per_serving / 0.30).toFixed(2)}</p>
              <p className="text-[9px] text-slate-500">@ 30% food cost</p>
            </div>
          </div>
          {/* Category breakdown if available */}
          {sop.cost_by_category && Object.keys(sop.cost_by_category).length > 0 && (
            <div className="mt-3 pt-3 border-t border-lime/10">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-2">Cost by Category</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(sop.cost_by_category).map(([cat, cost]) => (
                  <span key={cat} className="text-[10px] px-2 py-1 rounded bg-indigo-700/30 text-slate-300">
                    {cat}: <span className="text-gold font-bold">${cost.toFixed(2)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

// SOP Library Item
const SOPLibraryItem = ({ sop, onView, onDelete }) => {
  const dept = DEPARTMENTS[sop.department] || DEPARTMENTS.boh;
  const DeptIcon = dept.icon;

  return (
    <motion.div 
      className="glass-card rounded-lg p-4 border border-gold/10 hover:border-gold/30 transition-colors cursor-pointer"
      whileHover={{ scale: 1.02 }}
      onClick={() => onView(sop)}
      data-testid={`sop-item-${sop.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${dept.bg} flex items-center justify-center`}>
            <DeptIcon className={`w-5 h-5 ${dept.color}`} />
          </div>
          <div>
            <h4 className="font-inter font-medium text-white text-sm">{sop.title}</h4>
            <p className="text-xs text-slate-400">{dept.label} · {sop.created_at?.slice(0, 10)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-lime font-bold text-sm">${sop.total_cost?.toFixed(2) || "0"}</span>
          <Button
            onClick={(e) => { e.stopPropagation(); onDelete(sop.id); }}
            variant="ghost"
            size="sm"
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            data-testid={`delete-sop-${sop.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Audit Modal Component
const AuditModal = ({ isOpen, onClose, flags, safetyScore, costStatus, canSave, onConfirm, onEdit, isLoading, onAutoFix, editedSop }) => {
  const [fixingIndex, setFixingIndex] = useState(null);
  
  if (!isOpen) return null;

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case "CRITICAL":
        return { bg: "bg-red-500/10", border: "border-red-500/30", icon: XCircle, color: "text-red-400" };
      case "WARNING":
        return { bg: "bg-yellow-500/10", border: "border-yellow-500/30", icon: AlertCircle, color: "text-yellow-400" };
      default:
        return { bg: "bg-blue-500/10", border: "border-blue-500/30", icon: Info, color: "text-blue-400" };
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "SAFETY": return Shield;
      case "COST": return DollarSign;
      default: return FileText;
    }
  };

  // Determine if a flag can be auto-fixed
  const getAutoFixData = (flag) => {
    if (flag.category === "SAFETY" && flag.title?.includes("Cooking Temperature")) {
      // Extract temperatures from message
      const match = flag.message?.match(/(\d+)°F.*?(\d+)°F/);
      if (match) {
        const protein = flag.title?.replace("Unsafe Cooking Temperature for ", "").replace("No Temperature Specified for ", "");
        return {
          canFix: true,
          type: "temperature",
          data: { old_temp: parseInt(match[1]), new_temp: parseInt(match[2]), protein }
        };
      }
    }
    if (flag.category === "COST" && flag.title?.includes("Food Cost Exceeds")) {
      // Extract suggested price
      const match = flag.suggestion?.match(/\$(\d+\.?\d*)/);
      if (match) {
        return {
          canFix: true,
          type: "menu_price",
          data: { suggested_price: parseFloat(match[1]) }
        };
      }
    }
    if (flag.category === "SAFETY" && flag.title?.includes("Allergen")) {
      // Extract allergen
      const match = flag.message?.match(/declaration: (.+)/);
      if (match) {
        const allergens = match[1].split(", ");
        return {
          canFix: true,
          type: "allergen",
          data: { allergen: allergens[0] }
        };
      }
    }
    return { canFix: false };
  };

  const handleAutoFix = async (index, flag) => {
    const fixData = getAutoFixData(flag);
    if (!fixData.canFix) return;
    
    setFixingIndex(index);
    await onAutoFix(fixData.type, fixData.data);
    setFixingIndex(null);
  };

  const criticalCount = flags.filter(f => f.severity === "CRITICAL").length;
  const warningCount = flags.filter(f => f.severity === "WARNING").length;
  const infoCount = flags.filter(f => f.severity === "INFO").length;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#040F49] border border-gold/20 rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
          onClick={e => e.stopPropagation()}
          data-testid="audit-modal"
        >
          {/* Header */}
          <div className="p-5 border-b border-gold/15 bg-[#060D3A]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  criticalCount > 0 ? "bg-red-500/20" : warningCount > 0 ? "bg-yellow-500/20" : "bg-lime/20"
                }`}>
                  {criticalCount > 0 ? (
                    <XCircle className="w-6 h-6 text-red-400" />
                  ) : warningCount > 0 ? (
                    <AlertTriangle className="w-6 h-6 text-yellow-400" />
                  ) : (
                    <CheckCircle2 className="w-6 h-6 text-lime" />
                  )}
                </div>
                <div>
                  <h3 className="font-inter text-lg font-bold text-white">SOP Safety & Cost Audit</h3>
                  <div className="flex items-center gap-3 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      safetyScore >= 80 ? "bg-lime/10 text-lime" : 
                      safetyScore >= 50 ? "bg-yellow-500/10 text-yellow-400" : 
                      "bg-red-500/10 text-red-400"
                    }`}>
                      Safety: {safetyScore}%
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      costStatus === "OK" ? "bg-lime/10 text-lime" :
                      costStatus === "WARNING" ? "bg-yellow-500/10 text-yellow-400" :
                      "bg-red-500/10 text-red-400"
                    }`}>
                      Cost: {costStatus}
                    </span>
                  </div>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-indigo-500/20 text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Summary Bar */}
          <div className="px-5 py-3 bg-indigo-700/20 flex items-center gap-4 text-xs">
            {criticalCount > 0 && (
              <span className="flex items-center gap-1 text-red-400">
                <XCircle className="w-3 h-3" /> {criticalCount} Critical
              </span>
            )}
            {warningCount > 0 && (
              <span className="flex items-center gap-1 text-yellow-400">
                <AlertCircle className="w-3 h-3" /> {warningCount} Warning
              </span>
            )}
            {infoCount > 0 && (
              <span className="flex items-center gap-1 text-blue-400">
                <Info className="w-3 h-3" /> {infoCount} Info
              </span>
            )}
            {flags.length === 0 && (
              <span className="flex items-center gap-1 text-lime">
                <CheckCircle2 className="w-3 h-3" /> All checks passed!
              </span>
            )}
          </div>

          {/* Flags List */}
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            {flags.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle2 className="w-16 h-16 text-lime mx-auto mb-4" />
                <h4 className="text-lg font-bold text-white mb-2">Looking Good!</h4>
                <p className="text-sm text-slate-400">No safety or cost issues detected. Ready to save.</p>
              </div>
            ) : (
              flags.map((flag, i) => {
                const styles = getSeverityStyles(flag.severity);
                const SeverityIcon = styles.icon;
                const CategoryIcon = getCategoryIcon(flag.category);
                const fixData = getAutoFixData(flag);
                
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className={`${styles.bg} ${styles.border} border rounded-lg p-4 ${flag.severity === "CRITICAL" ? "animate-pulse-slow" : ""}`}
                    data-testid={`audit-flag-${i}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex items-center gap-2 shrink-0">
                        <SeverityIcon className={`w-5 h-5 ${styles.color}`} />
                        <CategoryIcon className="w-4 h-4 text-slate-500" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-bold uppercase tracking-wider ${styles.color}`}>
                              {flag.severity}
                            </span>
                            <span className="text-xs text-slate-500">•</span>
                            <span className="text-xs text-slate-400">{flag.category}</span>
                          </div>
                          {/* Auto-Fix Button */}
                          {fixData.canFix && (
                            <motion.button
                              onClick={() => handleAutoFix(i, flag)}
                              disabled={fixingIndex === i}
                              className={`flex items-center gap-1 text-[10px] px-3 py-1 rounded-full font-bold transition-all ${
                                flag.severity === "CRITICAL" 
                                  ? "bg-lime text-indigo-700 hover:bg-lime/80 animate-pulse" 
                                  : "bg-gold/20 text-gold hover:bg-gold/30"
                              }`}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              data-testid={`auto-fix-${i}`}
                            >
                              {fixingIndex === i ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Sparkles className="w-3 h-3" />
                              )}
                              Auto-Fix
                            </motion.button>
                          )}
                        </div>
                        <h5 className="text-sm font-bold text-white mb-1">{flag.title}</h5>
                        <p className="text-xs text-slate-300 leading-relaxed">{flag.message}</p>
                        {flag.suggestion && (
                          <p className="text-xs text-gold mt-2 flex items-start gap-1">
                            <Sparkles className="w-3 h-3 shrink-0 mt-0.5" />
                            {flag.suggestion}
                          </p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>

          {/* Footer Actions */}
          <div className="p-5 border-t border-gold/15 bg-[#060D3A] flex items-center justify-between">
            <p className="text-xs text-slate-500">
              {canSave 
                ? "You can save with warnings, but critical safety issues must be resolved." 
                : "Please fix critical safety issues before saving."}
            </p>
            <div className="flex items-center gap-3">
              <Button
                onClick={onEdit}
                variant="outline"
                className="border-slate-500 text-slate-300"
                data-testid="audit-edit-btn"
              >
                <Edit3 className="w-4 h-4 mr-2" /> Go Back & Edit
              </Button>
              <Button
                onClick={onConfirm}
                disabled={!canSave || isLoading}
                className={canSave ? "bg-lime hover:bg-lime/80 text-indigo-700" : "bg-slate-600 text-slate-400 cursor-not-allowed"}
                data-testid="audit-confirm-btn"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {flags.length === 0 ? "Save to Library" : "Confirm & Save Anyway"}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Argus Guidance Overlay for Guest Sandbox Mode
const ArgusGuidanceOverlay = ({ step, isGuest, onDismiss }) => {
  if (!isGuest) return null;
  
  const guidance = {
    welcome: {
      title: "Welcome to the Sandbox",
      message: "I'm Argus. Press the mic button and tell me a recipe. Try to get a temperature wrong — I'll catch it.",
      icon: Eye,
    },
    recording: {
      title: "Recording...",
      message: "Speak naturally. Mention ingredients, quantities, and steps. I'll structure everything automatically.",
      icon: Mic,
    },
    processing: {
      title: "Processing...",
      message: "Transcribing your audio and structuring the SOP. Watch the cost math calculate in real-time.",
      icon: Loader2,
    },
    review: {
      title: "Review Your SOP",
      message: "Check the ingredients and steps. Click 'Review & Save' to see the safety audit in action.",
      icon: Shield,
    },
    audit: {
      title: "Safety & Cost Audit",
      message: "This is where the magic happens. See any issues? Try the Auto-Fix buttons to fix them instantly.",
      icon: Sparkles,
    },
    success: {
      title: "You just built a Self-Driving SOP!",
      message: "Imagine this for every recipe, every training guide. Ready to claim your Founding Member spot?",
      icon: Crown,
    },
  };
  
  const currentGuide = guidance[step] || guidance.welcome;
  const Icon = currentGuide.icon;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-md"
      >
        <div className="glass-card rounded-xl border border-gold/30 bg-[#040F49]/95 backdrop-blur-xl p-4 shadow-2xl">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gold/20 flex items-center justify-center shrink-0">
              <Icon className={`w-5 h-5 text-gold ${step === 'processing' ? 'animate-spin' : ''}`} />
            </div>
            <div className="flex-1">
              <h4 className="font-inter font-bold text-gold text-sm">{currentGuide.title}</h4>
              <p className="text-xs text-slate-300 mt-1">{currentGuide.message}</p>
            </div>
            {onDismiss && (
              <button onClick={onDismiss} className="text-slate-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Founding Member Conversion Modal - High-Urgency Closing Pitch
const FoundingMemberModal = ({ isOpen, onClose, onConvert, remaining = 27, savingsDetected = null, risksFound = 0 }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [restaurant, setRestaurant] = useState("");
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState("");
  const [timeLeft, setTimeLeft] = useState(null);
  
  // Urgency countdown timer (15 minutes)
  useEffect(() => {
    if (isOpen && !timeLeft) {
      setTimeLeft(15 * 60); // 15 minutes
    }
  }, [isOpen]);
  
  useEffect(() => {
    if (!timeLeft || timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft(t => t - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  if (!isOpen) return null;

  // Pricing
  const regularPrice = 499;
  const foundingPrice = Math.round(regularPrice * 0.75); // 25% off = $374
  const savings = regularPrice - foundingPrice;

  const handleConvert = async () => {
    if (!name.trim() || !email.trim()) {
      setError("Please enter your name and email");
      return;
    }
    if (!email.includes("@")) {
      setError("Please enter a valid email");
      return;
    }
    
    setIsConverting(true);
    setError("");
    
    try {
      await onConvert(name, email, restaurant);
    } catch (err) {
      setError(err.message || "Failed to complete signup");
      setIsConverting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 30 }}
          transition={{ type: "spring", damping: 25 }}
          className="bg-gradient-to-b from-[#0A1654] to-[#040F49] border-2 border-gold rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl shadow-gold/20"
          onClick={e => e.stopPropagation()}
          data-testid="founding-member-modal"
        >
          {/* Urgency Header */}
          {timeLeft && timeLeft > 0 && (
            <div className="bg-red-500 py-2 px-4 flex items-center justify-center gap-2">
              <span className="text-white text-sm font-bold animate-pulse">⏰ Offer expires in</span>
              <span className="bg-white text-red-500 px-2 py-0.5 rounded font-mono font-bold">
                {formatTime(timeLeft)}
              </span>
            </div>
          )}
          
          {/* Header with crown */}
          <div className="relative bg-gradient-to-r from-gold/30 to-gold/10 p-6 border-b border-gold/30">
            <div className="absolute top-0 right-0 w-32 h-32 opacity-20">
              <Crown className="w-full h-full text-gold" />
            </div>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-gold flex items-center justify-center shadow-lg shadow-gold/30">
                <Crown className="w-8 h-8 text-indigo-700" />
              </div>
              <div>
                <h2 className="font-inter text-2xl font-black text-white">You Did It.</h2>
                <p className="text-gold text-sm font-bold">Only {remaining} Founding spots left</p>
              </div>
            </div>
          </div>
          
          {/* Dynamic Value Message */}
          <div className="p-6 border-b border-gold/20 bg-lime/5">
            <p className="text-white text-base leading-relaxed">
              I've just <span className="text-lime font-bold">secured your margins</span>
              {risksFound > 0 && <span> and caught <span className="text-red-400 font-bold">{risksFound} safety risk{risksFound > 1 ? 's' : ''}</span></span>}.
              {savingsDetected && <span> You're looking at <span className="text-lime font-bold">{savingsDetected}% food cost savings</span> on this dish alone.</span>}
            </p>
            <p className="text-slate-400 text-sm mt-2">
              Imagine this for every recipe, every station, 24/7.
            </p>
          </div>
          
          {/* Pricing Comparison */}
          <div className="p-6 border-b border-gold/10">
            <div className="grid grid-cols-2 gap-4">
              {/* Founding Price */}
              <div className="bg-gradient-to-br from-gold/20 to-gold/5 rounded-xl p-4 border-2 border-gold relative overflow-hidden">
                <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-bl">
                  LIMITED
                </div>
                <p className="text-xs text-gold font-bold uppercase tracking-wider">Founding 50</p>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="text-3xl font-black text-white">${foundingPrice}</span>
                  <span className="text-slate-400 text-sm">/mo</span>
                </div>
                <p className="text-lime text-xs font-bold mt-1">+ First month FREE</p>
                <p className="text-[10px] text-slate-500 mt-1">Locked for life</p>
              </div>
              
              {/* Regular Price */}
              <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700 relative">
                <div className="absolute inset-0 bg-gradient-to-br from-transparent to-slate-900/50" />
                <p className="text-xs text-slate-500 font-bold uppercase tracking-wider relative">After Launch</p>
                <div className="flex items-baseline gap-1 mt-1 relative">
                  <span className="text-3xl font-black text-slate-500 line-through">${regularPrice}</span>
                  <span className="text-slate-600 text-sm">/mo</span>
                </div>
                <p className="text-slate-600 text-xs mt-1">No first month free</p>
                <p className="text-[10px] text-slate-700 mt-1">Standard pricing</p>
              </div>
            </div>
            
            <div className="mt-4 bg-lime/10 rounded-lg p-3 border border-lime/30 text-center">
              <p className="text-lime font-bold">
                You save ${savings}/mo forever + ${foundingPrice} first month = <span className="text-white">${savings * 12 + foundingPrice}/year</span>
              </p>
            </div>
          </div>
          
          {/* ROI Stats */}
          <div className="px-6 py-4 grid grid-cols-3 gap-2 border-b border-gold/10">
            <div className="text-center">
              <p className="text-lime font-bold text-lg">Voice</p>
              <p className="text-[10px] text-slate-500">To SOP</p>
            </div>
            <div className="text-center border-x border-gold/20">
              <p className="text-gold font-bold text-lg">AI</p>
              <p className="text-[10px] text-slate-500">Cost analysis</p>
            </div>
            <div className="text-center">
              <p className="text-white font-bold text-lg">V1</p>
              <p className="text-[10px] text-slate-500">Available now</p>
            </div>
          </div>
          
          {/* Form */}
          <div className="p-6 space-y-3">
            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-sm text-red-400">
                {error}
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400 text-xs">Your Name *</Label>
                <Input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Chef John"
                  className="mt-1 bg-indigo-700/50 border-gold/30 text-white"
                  data-testid="founder-name-input"
                />
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Restaurant</Label>
                <Input
                  value={restaurant}
                  onChange={e => setRestaurant(e.target.value)}
                  placeholder="Your restaurant"
                  className="mt-1 bg-indigo-700/50 border-gold/30 text-white"
                  data-testid="founder-restaurant-input"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-slate-400 text-xs">Email *</Label>
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="chef@restaurant.com"
                className="mt-1 bg-indigo-700/50 border-gold/30 text-white"
                data-testid="founder-email-input"
              />
            </div>
            
            <Button
              onClick={handleConvert}
              disabled={isConverting}
              className="w-full bg-gradient-to-r from-gold to-amber-500 hover:from-gold/90 hover:to-amber-500/90 text-indigo-900 font-black py-4 text-lg shadow-lg shadow-gold/30 mt-2"
              data-testid="claim-spot-btn"
            >
              {isConverting ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Crown className="w-5 h-5 mr-2" />
              )}
              Claim Founding Spot #{50 - remaining + 1}
            </Button>
            
            <button
              onClick={onClose}
              className="w-full text-xs text-slate-600 hover:text-slate-400 py-2"
            >
              I'll pay ${regularPrice}/mo later (no first month free)
            </button>
            
            <p className="text-[10px] text-slate-700 text-center">
              Cancel anytime • No contracts • 30-day money-back guarantee
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Main SOP Builder Page
export default function SOPBuilder() {
  const [searchParams] = useSearchParams();
  const { isGuestMode, convertToMember, endGuestSession, guestSessionId } = useAuth();
  
  const [view, setView] = useState("create"); // create | library | detail
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentSop, setCurrentSop] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSop, setEditedSop] = useState(null);
  const [library, setLibrary] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("boh");
  
  // Audit Modal State
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [isAuditing, setIsAuditing] = useState(false);
  
  // Guest Mode / Sandbox State
  const isSandbox = searchParams.get("sandbox") === "true" || isGuestMode;
  const [guidanceStep, setGuidanceStep] = useState("welcome");
  const [showGuidance, setShowGuidance] = useState(isSandbox);
  const [showFoundingModal, setShowFoundingModal] = useState(false);
  const [hasCompletedDemo, setHasCompletedDemo] = useState(false);
  const [remainingSpots, setRemainingSpots] = useState(27);
  
  // Fetch remaining founding spots
  useEffect(() => {
    const fetchSpots = async () => {
      try {
        const res = await axios.get(`${API}/founding-spots`);
        setRemainingSpots(res.data.remaining);
      } catch {
        // Use default
      }
    };
    fetchSpots();
  }, []);

  // Fetch SOP library
  const fetchLibrary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sops`);
      setLibrary(res.data.sops || []);
    } catch {
      // Library might be empty
    }
  }, []);

  useEffect(() => {
    fetchLibrary();
  }, [fetchLibrary]);

  // Process audio (record or upload)
  const processAudio = async (audioBlob) => {
    setIsProcessing(true);
    if (isSandbox) setGuidanceStep("processing");
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('department', selectedDepartment);

      const res = await axios.post(`${API}/sops/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.sop) {
        setCurrentSop(res.data.sop);
        setEditedSop(res.data.sop);
        setIsEditing(true);
        toast.success("Audio transcribed! Review and edit your SOP.");
        if (isSandbox) setGuidanceStep("review");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process audio");
      if (isSandbox) setGuidanceStep("welcome");
    } finally {
      setIsProcessing(false);
    }
  };

  // Save SOP to library (with audit)
  const initiateSave = async () => {
    if (!editedSop?.title) {
      toast.error("Please add a title for your SOP");
      return;
    }
    
    // Run audit first
    setIsAuditing(true);
    if (isSandbox) setGuidanceStep("audit");
    
    try {
      const auditRes = await axios.post(`${API}/sops/audit`, {
        sop: editedSop,
        check_safety: true,
        check_cost: true,
        check_consistency: true,
      });
      setAuditResult(auditRes.data);
      setShowAuditModal(true);
    } catch (err) {
      toast.error("Audit failed. Saving without checks...");
      // Fallback: save without audit
      await confirmSave();
    } finally {
      setIsAuditing(false);
    }
  };

  const confirmSave = async () => {
    setIsSaving(true);
    try {
      const res = await axios.post(`${API}/sops`, editedSop);
      toast.success("SOP saved to library!");
      setCurrentSop(res.data.sop);
      setIsEditing(false);
      setShowAuditModal(false);
      setAuditResult(null);
      fetchLibrary();
      
      // In sandbox mode, show the success and trigger founding member modal
      if (isSandbox && !hasCompletedDemo) {
        setHasCompletedDemo(true);
        setGuidanceStep("success");
        // Show the founding member modal after a short celebration
        setTimeout(() => {
          setShowFoundingModal(true);
          setShowGuidance(false);
        }, 2000);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save SOP");
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle founding member conversion
  const handleFoundingConversion = async (name, email, restaurant) => {
    const result = await convertToMember(name, email, restaurant);
    
    if (result.success) {
      setShowFoundingModal(false);
      toast.success(`Welcome, Founding Member #${result.memberNumber}! Redirecting...`);
      setRemainingSpots(result.remainingSpots);
      
      // Redirect to thank you page
      setTimeout(() => {
        window.location.href = `/thank-you?member=${result.memberNumber}`;
      }, 2000);
    } else {
      throw new Error(result.error);
    }
  };

  const closeAuditAndEdit = () => {
    setShowAuditModal(false);
    setAuditResult(null);
  };

  // Auto-Fix handler
  const handleAutoFix = async (fixType, fixData) => {
    try {
      const res = await axios.post(`${API}/sops/auto-fix`, {
        sop: editedSop,
        fix_type: fixType,
        fix_data: fixData,
      });
      
      if (res.data.success) {
        // Update the edited SOP with the fix
        setEditedSop(res.data.sop);
        toast.success(res.data.message);
        
        // Re-run audit to update flags
        const auditRes = await axios.post(`${API}/sops/audit`, {
          sop: res.data.sop,
          check_safety: true,
          check_cost: true,
          check_consistency: true,
        });
        setAuditResult(auditRes.data);
      } else {
        toast.error(res.data.message || "Auto-fix failed");
      }
    } catch {
      toast.error("Failed to apply auto-fix");
    }
  };

  // Delete SOP
  const deleteSop = async (id) => {
    if (!window.confirm("Delete this SOP?")) return;
    try {
      await axios.delete(`${API}/sops/${id}`);
      toast.success("SOP deleted");
      fetchLibrary();
      if (currentSop?.id === id) {
        setCurrentSop(null);
        setView("library");
      }
    } catch {
      toast.error("Failed to delete SOP");
    }
  };

  // View SOP detail
  const viewSop = (sop) => {
    setCurrentSop(sop);
    setEditedSop(sop);
    setIsEditing(false);
    setView("detail");
  };

  // Export PDF
  const exportPdf = async () => {
    if (!currentSop?.id) return;
    try {
      const res = await axios.get(`${API}/sops/${currentSop.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${currentSop.title?.replace(/\s+/g, '_') || 'sop'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch {
      toast.error("Failed to export PDF");
    }
  };

  return (
    <div className="min-h-screen bg-[#040F49]" data-testid="sop-builder-page">
      <div className="noise-overlay" />
      
      {/* Argus Guidance Overlay for Sandbox Mode */}
      {showGuidance && (
        <ArgusGuidanceOverlay 
          step={guidanceStep} 
          isGuest={isSandbox}
          onDismiss={() => setShowGuidance(false)}
        />
      )}
      
      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-indigo-700/90 border-b border-gold/20">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldLogo size="sm" />
            <span className="font-inter text-sm font-bold text-gold tracking-wider">MASTERCLASS SOP BUILDER</span>
            {isSandbox && (
              <span className="px-2 py-0.5 rounded-full bg-lime/20 text-lime text-[10px] font-bold uppercase tracking-wider">
                Sandbox Mode
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => { setView("create"); setCurrentSop(null); setIsEditing(false); if (isSandbox) setGuidanceStep("welcome"); }}
              variant={view === "create" ? "default" : "ghost"}
              size="sm"
              className={view === "create" ? "bg-gold text-indigo-700" : "text-slate-400 hover:text-white"}
              data-testid="nav-create"
            >
              <Plus className="w-4 h-4 mr-1" /> Create
            </Button>
            {!isSandbox && (
              <Button
                onClick={() => setView("library")}
                variant={view === "library" ? "default" : "ghost"}
                size="sm"
                className={view === "library" ? "bg-gold text-indigo-700" : "text-slate-400 hover:text-white"}
                data-testid="nav-library"
              >
                <FileText className="w-4 h-4 mr-1" /> Library ({library.length})
              </Button>
            )}
            <a href="/" className="ml-4 text-slate-400 hover:text-white">
              <Home className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Create View */}
        {view === "create" && !currentSop && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            <div className="text-center">
              <h1 className="font-inter text-3xl font-black text-white mb-2">
                Record Your <span className="text-gold">SOP</span>
              </h1>
              <p className="text-slate-400 max-w-md mx-auto">
                Speak your recipe, procedure, or training guide. AI will structure it into a professional SOP with cost analysis.
              </p>
            </div>

            {/* Department Selector */}
            <div className="flex justify-center gap-2">
              {Object.entries(DEPARTMENTS).map(([key, { label, icon: Icon, color, bg }]) => (
                <button
                  key={key}
                  onClick={() => setSelectedDepartment(key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                    selectedDepartment === key 
                      ? `${bg} border-gold/40 ${color}` 
                      : "border-slate-600 text-slate-400 hover:border-slate-500"
                  }`}
                  data-testid={`dept-${key}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>

            {/* Recording Area */}
            <div className="glass-card rounded-xl p-8 border border-gold/20 flex flex-col items-center">
              <AudioRecorder onRecordingComplete={processAudio} isProcessing={isProcessing} />
              
              <div className="flex items-center gap-4 my-6">
                <div className="h-px w-16 bg-slate-600" />
                <span className="text-xs text-slate-500">OR</span>
                <div className="h-px w-16 bg-slate-600" />
              </div>

              <FileUploader onFileSelect={processAudio} isProcessing={isProcessing} />
            </div>

            {/* Tips */}
            <div className="glass-card rounded-xl p-4 border border-gold/10 bg-gold/5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-bold text-gold mb-1">Pro Tips for Best Results</h4>
                  <ul className="text-xs text-slate-300 space-y-1">
                    <li>• Start with the name: "This is our House Marinara recipe..."</li>
                    <li>• Mention quantities clearly: "Two cups of flour, one tablespoon of salt..."</li>
                    <li>• Describe each step in order: "First, preheat the oven... Then..."</li>
                    <li>• Include yield: "This makes about 10 servings..."</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* SOP Preview/Edit View */}
        {(view === "create" || view === "detail") && currentSop && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between">
              <Button
                onClick={() => { setView("create"); setCurrentSop(null); setIsEditing(false); }}
                variant="ghost"
                className="text-slate-400 hover:text-white"
              >
                ← New SOP
              </Button>
              {currentSop.id && !isEditing && (
                <Button onClick={exportPdf} variant="outline" className="border-gold/30 text-gold hover:bg-gold/10" data-testid="export-pdf-btn">
                  <Download className="w-4 h-4 mr-2" /> Export PDF
                </Button>
              )}
            </div>

            <SOPPreviewCard
              sop={isEditing ? editedSop : currentSop}
              onSave={initiateSave}
              onEdit={() => { setEditedSop(currentSop); setIsEditing(true); }}
              isEditing={isEditing}
              editedSop={editedSop}
              setEditedSop={setEditedSop}
              onCancel={() => { setIsEditing(false); if (!currentSop.id) setCurrentSop(null); }}
              isSaving={isSaving}
              isAuditing={isAuditing}
            />
          </motion.div>
        )}

        {/* Library View */}
        {view === "library" && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-inter text-xl font-bold text-white">SOP Library</h2>
              <span className="text-sm text-slate-400">{library.length} SOPs</span>
            </div>

            {library.length === 0 ? (
              <div className="glass-card rounded-xl p-12 border border-gold/10 text-center">
                <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No SOPs Yet</h3>
                <p className="text-sm text-slate-400 mb-4">Record your first SOP to build your training library.</p>
                <Button onClick={() => setView("create")} className="bg-gold hover:bg-gold/80 text-indigo-700">
                  <Plus className="w-4 h-4 mr-2" /> Create SOP
                </Button>
              </div>
            ) : (
              <div className="grid gap-3">
                {library.map(sop => (
                  <SOPLibraryItem key={sop.id} sop={sop} onView={viewSop} onDelete={deleteSop} />
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Audit Modal */}
      <AuditModal
        isOpen={showAuditModal}
        onClose={closeAuditAndEdit}
        flags={auditResult?.flags || []}
        safetyScore={auditResult?.safety_score || 100}
        costStatus={auditResult?.cost_status || "OK"}
        canSave={auditResult?.can_save !== false}
        onConfirm={confirmSave}
        onEdit={closeAuditAndEdit}
        isLoading={isSaving}
        onAutoFix={handleAutoFix}
        editedSop={editedSop}
      />
      
      {/* Founding Member Conversion Modal (Sandbox Mode) */}
      <FoundingMemberModal
        isOpen={showFoundingModal}
        onClose={() => setShowFoundingModal(false)}
        onConvert={handleFoundingConversion}
        remaining={remainingSpots}
        savingsDetected={auditResult?.cost_savings_percent}
        risksFound={auditResult?.flags?.filter(f => f.severity === "CRITICAL").length || 0}
      />
    </div>
  );
}
